import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Mail, Edit3, Eye, ExternalLink, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import RichTextComposer from "./RichTextComposer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modus "Vorschau" (default) zeigt den Inhalt mit bereits ersetzten Variablen —
 * der Vertriebler sieht direkt was beim Kunden ankommt, keine `{{firma}}`-Klammern.
 *
 * Modus "Bearbeiten" enthüllt den Editor mit Variablen-Chips.
 */
export default function SendEmailModal({ open, onClose, leadId, leadEmail, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("_custom");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [preview, setPreview] = useState({ subject: "", body_html: "", demo_link: "" });
  const [mode, setMode] = useState("preview"); // 'preview' | 'edit'
  const [sending, setSending] = useState(false);
  const debounceRef = useRef(null);

  // Beim Öffnen: Templates laden, Form resetten
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/email-templates`, { withCredentials: true }).then(r => {
      setTemplates(r.data);
      // Auto-Pick: Angebot-Template wenn vorhanden
      const offer = r.data.find(t => t.seed_key === "default_offer_with_demo");
      if (offer) {
        setTplId(offer.id);
      }
    });
    setSubject("");
    setBodyHtml("");
    setPreview({ subject: "", body_html: "", demo_link: "" });
    setMode("preview");
    setTplId("_custom");
  }, [open]);

  // Template-Wechsel → Felder befüllen
  useEffect(() => {
    if (tplId === "_custom") {
      setSubject("");
      setBodyHtml("");
      return;
    }
    const t = templates.find(t => t.id === tplId);
    if (t) {
      setSubject(t.subject);
      setBodyHtml(t.body_html || "");
    }
  }, [tplId, templates]);

  // Live-Preview-Refresh: 350ms debounce sobald subject/bodyHtml/tplId sich ändern
  useEffect(() => {
    if (!open || !leadId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${API}/leads/${leadId}/preview-email`,
          { template_id: tplId !== "_custom" ? tplId : null, subject, body_html: bodyHtml },
          { withCredentials: true }
        );
        setPreview({
          subject: r.data.subject || "",
          body_html: r.data.body_html || "",
          demo_link: r.data.demo_link || "",
        });
      } catch {
        // ignore — Preview ist nice-to-have
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [open, leadId, tplId, subject, bodyHtml]);

  const doSend = async () => {
    if (!leadEmail) { toast.error("Lead hat keine E-Mail-Adresse"); return; }
    if (!subject.trim()) { toast.error("Betreff fehlt"); return; }
    if (!bodyHtml.trim()) { toast.error("Inhalt fehlt"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/leads/${leadId}/send-email`,
        { template_id: tplId !== "_custom" ? tplId : null, subject, body_html: bodyHtml },
        { withCredentials: true });
      if (r.data.sent) {
        toast.success(`E-Mail an ${r.data.to} versandt`);
      } else {
        toast.warning(`Nicht gesendet (${r.data.reason || "Resend nicht konfiguriert"}) — als Activity geloggt`);
      }
      onSent?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSending(false); }
  };

  const isOfferTpl = templates.find(t => t.id === tplId)?.seed_key === "default_offer_with_demo";
  const showDemoWarning = isOfferTpl && !preview.demo_link;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="send-email-modal">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold flex items-center gap-2">
            <Mail size={16} /> E-Mail an {leadEmail || "(keine E-Mail)"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Template-Picker */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Vorlage wählen</Label>
            <Select value={tplId} onValueChange={setTplId}>
              <SelectTrigger className="rounded-sm" data-testid="email-tpl-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_custom">— Freier Text —</SelectItem>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.is_default && <span className="text-[9px] text-muted-foreground ml-2">Standard</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Demo-Link Hinweis bei Angebots-Template */}
          {isOfferTpl && preview.demo_link && (
            <div className="px-3 py-2 bg-emerald-50 border border-emerald-300 rounded-sm dark:bg-emerald-950/30 dark:border-emerald-900 flex items-center gap-2 text-xs" data-testid="demo-link-hint">
              <Sparkles size={13} className="text-emerald-700 dark:text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-bold text-emerald-900 dark:text-emerald-100">Demo-Link automatisch eingesetzt:</span>{" "}
                <a href={preview.demo_link} target="_blank" rel="noreferrer" className="text-emerald-700 dark:text-emerald-400 underline truncate inline-flex items-center gap-1">
                  {preview.demo_link.replace(/^https?:\/\//, "").slice(0, 60)}…
                  <ExternalLink size={10} />
                </a>
              </div>
            </div>
          )}
          {showDemoWarning && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-300 rounded-sm dark:bg-amber-950/30 dark:border-amber-900 text-xs" data-testid="no-demo-warning">
              <span className="font-bold text-amber-900 dark:text-amber-100">⚠ Kein Demo-Link verfügbar</span>{" "}
              <span className="text-amber-800 dark:text-amber-200">— Lead hat noch keine Demo-Website. Erst „Demo bauen", dann diese Vorlage nutzen.</span>
            </div>
          )}

          {/* Betreff */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Betreff</Label>
            {mode === "preview" ? (
              <div className="rounded-sm border border-border bg-muted/20 px-3 py-2 text-sm font-medium" data-testid="subject-preview">
                {preview.subject || <span className="text-muted-foreground italic">Wird mit Vorlage gefüllt …</span>}
              </div>
            ) : (
              <Input value={subject} onChange={e => setSubject(e.target.value)} className="rounded-sm" data-testid="email-subject" />
            )}
          </div>

          {/* Inhalt — Mode Toggle */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                {mode === "preview" ? "Vorschau (so sieht's der Kunde)" : "Inhalt anpassen"}
              </Label>
              <div className="inline-flex items-center gap-0.5 p-0.5 rounded-sm bg-muted">
                <button
                  type="button"
                  onClick={() => setMode("preview")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wide transition ${
                    mode === "preview" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="mode-preview-btn"
                >
                  <Eye size={11} /> Vorschau
                </button>
                <button
                  type="button"
                  onClick={() => setMode("edit")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wide transition ${
                    mode === "edit" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="mode-edit-btn"
                >
                  <Edit3 size={11} /> Anpassen
                </button>
              </div>
            </div>
            {mode === "preview" ? (
              <div
                className="rounded-sm border border-border bg-card p-4 prose prose-sm max-w-none dark:prose-invert min-h-[280px]"
                dangerouslySetInnerHTML={{ __html: preview.body_html || `<p class='text-muted-foreground italic'>Wähle eine Vorlage oder klicke auf "Anpassen", um Inhalt einzugeben.</p>` }}
                data-testid="body-preview"
              />
            ) : (
              <RichTextComposer
                value={bodyHtml}
                onChange={setBodyHtml}
                variables={["firma", "ansprechpartner", "anrede", "stadt", "vertriebler", "vertriebler_email", "vertriebler_phone", "telefon", "website", "demo_link"]}
                placeholder="Hallo {{ansprechpartner}}, …"
                testId="email-body-composer"
              />
            )}
          </div>

          {/* Hinweis bei Edit-Mode */}
          {mode === "edit" && (
            <p className="text-[11px] text-muted-foreground italic">
              Tipp: <code className="font-mono bg-muted px-1 py-0.5 rounded">{"{{firma}}"}</code>, <code className="font-mono bg-muted px-1 py-0.5 rounded">{"{{demo_link}}"}</code> usw. werden beim Senden automatisch ersetzt. Wechsle zu „Vorschau", um den finalen Text zu sehen.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
            <Button onClick={doSend} disabled={sending || !leadEmail || !subject || !bodyHtml} className="rounded-sm gap-1.5" data-testid="email-send-btn">
              <Send size={13} /> {sending ? "Sende..." : "Senden"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
